import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ChartContainer from "./ChartContainer";
import { useTheme } from '../../hooks/useTheme';
import { getChartColors } from '../../utils/chartColors';

interface LineChartData {
  date: string;
  value: number;
  label?: string;
}

interface LineChartProps {
  data: LineChartData[];
  title?: string;
  showLabels?: boolean;
  showStats?: boolean;
  unit?: string;
  dataType?: string;
  className?: string;
  height?: string;
}

const LineChartComponent: React.FC<LineChartProps> = ({
  data,
  title = "Line Chart",
  showLabels = true,
  showStats = true,
  unit = "",
  dataType = "number",
  className = "",
  height = "h-64",
}) => {
  const { isDark } = useTheme();
  const colors = getChartColors(isDark);
  const processedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      displayLabel:
        item.label ||
        new Date(item.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
    }));
  }, [data]);

  const stats = useMemo(() => {
    if (!showStats || data.length === 0) return null;

    const values = data.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

    return { min, max, avg, count: data.length };
  }, [data, showStats]);

  if (data.length === 0) {
    return (
      <ChartContainer title={title} className={className} height={height}>
        <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
          No data available
        </div>
      </ChartContainer>
    );
  }

  const formatValue = (value: number) => {
    if (dataType === 'time') {
      if (value < 60) return `${Math.round(value)}m`;
      const hours = Math.floor(value / 60);
      const mins = Math.round(value % 60);
      return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
    }
    return value.toString();
  };

  const chartData = processedData.map(item => ({
    date: item.displayLabel,
    value: item.value
  }));

  return (
    <>
      <ChartContainer title={title} className={className} height={height}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.grid}
            />
            <XAxis
              dataKey="date"
              stroke={colors.text}
              tick={{ fill: colors.text }}
            />
            <YAxis
              stroke={colors.text}
              tick={{ fill: colors.text }}
            />
            <Tooltip
              formatter={(value: number) => [`${formatValue(value)}${unit}`, 'Value']}
              labelFormatter={(label) => label}
              contentStyle={{
                backgroundColor: colors.tooltip.background,
                border: `1px solid ${colors.tooltip.border}`,
                borderRadius: '8px',
                color: colors.tooltip.text
              }}
            />
            <Line type="monotone" dataKey="value" stroke={colors.primary} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>

      {/* Stats */}
      {showStats && stats && (
        <div className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-xl">
          <h4 className="text-sm font-medium mb-3">Statistics</h4>
          <div className="text-sm space-y-2">
            <div className="flex justify-between">
              <span>Total entries:</span>
              <span className="font-medium">{stats.count}</span>
            </div>
            <div className="flex justify-between">
              <span>Range:</span>
              <span className="font-medium">
                {formatValue(stats.min)} - {formatValue(stats.max)}{unit}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Average:</span>
              <span className="font-medium">
                {dataType === 'time' ? formatValue(stats.avg) : stats.avg.toFixed(2)}{unit}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LineChartComponent;
